import { useEffect, type ReactNode } from 'react';
import { Authenticator, translations, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { I18n } from 'aws-amplify/utils';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { isAppConfigured, missingConfigKeys } from '../lib/config';
import { useAuth } from '../hooks/useAuth';
import { CognitoLogoutContext } from '../hooks/cognitoLogout';
import {
  displayNameFromClaims,
  initialsFromClaims,
  primaryCognitoGroup,
} from '../lib/identity';
import type { SessionUser } from '../lib/types';
import type { CognitoGroup } from '@respaldos/shared';

export const PRODUCT_NAME = 'Respaldos';
export const PRODUCT_SUB = 'ARCHIVOS INTERNOS';

I18n.putVocabularies(translations);
I18n.putVocabularies({
  es: {
    'Sign in': 'Entrar',
    Email: 'Correo',
    'Enter your Email': 'nombre@grupogalero.com',
    Password: 'Contraseña',
    'Enter your Password': '••••••••',
    'Forgot your password?': '¿Olvidó su contraseña?',
  },
});
I18n.setLanguage('es');

function AuthHeader() {
  return (
    <div className="auth-brand">
      <img className="login-logo" src="/logo.svg" alt="Grupo Galero" />
      <div className="topbar-product">{PRODUCT_NAME}</div>
      <div className="topbar-sub">{PRODUCT_SUB}</div>
    </div>
  );
}

function AuthFooter() {
  return <p className="login-foot">Acceso interno · Grupo Galero</p>;
}

const passwordRevealLabels = {
  showPasswordButtonLabel: 'Mostrar contraseña',
  passwordIsHiddenLabel: 'La contraseña está oculta',
  passwordIsShownLabel: 'La contraseña está visible',
};

function ConfigIncomplete() {
  const missing = missingConfigKeys();
  return (
    <div className="login-flat">
      <div className="login-stage">
        <div className="auth-brand">
          <img className="login-logo" src="/logo.svg" alt="Grupo Galero" />
          <div className="topbar-product">{PRODUCT_NAME}</div>
          <div className="topbar-sub">{PRODUCT_SUB}</div>
        </div>
        <div className="config-error" role="alert">
          <h1>Configuración incompleta</h1>
          <p>
            Este portal requiere Cognito y la API. No hay modo demostración ni
            datos de muestra.
          </p>
          <p>Defina estas variables al construir la app web:</p>
          <ul>
            {missing.map((k) => (
              <li key={k}>
                <code>{k}</code>
              </li>
            ))}
            {missing.length === 0 && (
              <>
                <li>
                  <code>VITE_API_URL</code>
                </li>
                <li>
                  <code>VITE_COGNITO_USER_POOL_ID</code>
                </li>
                <li>
                  <code>VITE_COGNITO_CLIENT_ID</code>
                </li>
              </>
            )}
          </ul>
          <p className="config-hint">
            Tras el deploy del stack CDK, copie <code>ApiUrl</code>,{' '}
            <code>UserPoolId</code> y <code>UserPoolClientId</code> a{' '}
            <code>apps/web/.env.production</code> (o <code>.env.local</code> para
            desarrollo), vuelva a construir y publique <code>dist/</code>.
          </p>
        </div>
        <p className="login-foot">Acceso interno · Grupo Galero</p>
      </div>
    </div>
  );
}

function CognitoSessionBridge({ children }: { children: ReactNode }) {
  const { user: amplifyUser, authStatus, signOut: amplifySignOut } =
    useAuthenticator((ctx) => [ctx.user, ctx.authStatus]);
  const { user, setCognitoSession, logout } = useAuth();

  useEffect(() => {
    if (authStatus !== 'authenticated' || !amplifyUser) return;
    let cancelled = false;
    (async () => {
      const session = await fetchAuthSession();
      const payload = session.tokens?.idToken?.payload || {};
      const groupsRaw = payload['cognito:groups'];
      const groups: CognitoGroup[] = Array.isArray(groupsRaw)
        ? (groupsRaw as CognitoGroup[])
        : typeof groupsRaw === 'string'
          ? (groupsRaw.split(',').map((g) => g.trim()) as CognitoGroup[])
          : [];
      const email = String(
        payload.email || amplifyUser.signInDetails?.loginId || '',
      );
      const claims = {
        name: payload.name,
        display_name: payload['custom:display_name'] ?? payload.display_name,
        given_name: payload.given_name,
        family_name: payload.family_name,
        email,
      };
      const name = displayNameFromClaims(claims, email);
      const sessionGroups = groups.length
        ? groups
        : (['Marketing'] as CognitoGroup[]);
      const sessionUser: SessionUser = {
        id: String(payload.sub || amplifyUser.userId),
        email,
        name,
        initials: initialsFromClaims(claims, email),
        groups: sessionGroups,
        color: '#EC2227',
        primaryGroup: primaryCognitoGroup(sessionGroups),
      };
      if (!cancelled) setCognitoSession(sessionUser);
    })().catch(console.error);
    return () => {
      cancelled = true;
    };
  }, [authStatus, amplifyUser, setCognitoSession]);

  useEffect(() => {
    if (authStatus === 'unauthenticated' && user) {
      logout();
    }
  }, [authStatus, user, logout]);

  if (authStatus !== 'authenticated' || !user) {
    return null;
  }

  return (
    <CognitoLogoutContext.Provider
      value={async () => {
        await amplifySignOut();
        await signOut().catch(() => undefined);
        logout();
      }}
    >
      {children}
    </CognitoLogoutContext.Provider>
  );
}

export function AuthGate({ children }: { children: ReactNode }) {
  if (!isAppConfigured()) {
    return <ConfigIncomplete />;
  }

  return (
    <Authenticator
      className="cognito-shell"
      hideSignUp
      loginMechanisms={['email']}
      variation="default"
      formFields={{
        signIn: {
          username: {
            label: 'Correo',
            placeholder: 'nombre@grupogalero.com',
          },
          password: {
            label: 'Contraseña',
            placeholder: '••••••••',
            ...passwordRevealLabels,
          },
        },
        forgotPassword: {
          username: {
            label: 'Correo',
            placeholder: 'nombre@grupogalero.com',
          },
        },
      }}
      components={{ Header: AuthHeader, Footer: AuthFooter }}
    >
      <CognitoSessionBridge>{children}</CognitoSessionBridge>
    </Authenticator>
  );
}
